/**
 * 交易活动 WebSocket Hook
 * 
 * 连接到 API WebSocket 端点，接收推荐交易员的实时交易活动
 */
import { useEffect, useState, useCallback, useRef } from 'react';

export interface TraderActivity {
  id: string;
  trader_address: string;
  trader_name: string | null;
  trader_stats?: {
    rov: number | null;
    win_rate: number | null;
    profit: number | null;
    total_volume?: number | null;
  };
  side: 'BUY' | 'SELL';
  price: number;
  size: number;
  outcome: string;
  market_title: string;
  market_slug: string | null;
  event_slug: string | null;
  condition_id: string;
  asset_id: string;
  timestamp: number;
  transaction_hash: string;
  received_at?: number;
}

interface WebSocketMessage {
  type: 'trader_activity';
  data: TraderActivity;
}

interface UseTraderActivityWebSocketReturn {
  activities: TraderActivity[];
  lastActivity: TraderActivity | null;
  isConnected: boolean;
  error: string | null;
}

// 从 API URL 动态生成 WebSocket URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
const WS_URL = API_BASE_URL.replace(/^http/, 'ws') + '/ws/activities';
const RECONNECT_DELAY = 3000; // 3 秒
const MAX_ACTIVITIES = 50; // 最多保存 50 条

export function useTraderActivityWebSocket(): UseTraderActivityWebSocketReturn {
  const [activities, setActivities] = useState<TraderActivity[]>([]);
  const [lastActivity, setLastActivity] = useState<TraderActivity | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const connect = useCallback(() => {
    try {
      // 清理旧连接
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('✅ WebSocket 已连接');
        setIsConnected(true);
        setError(null);
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          
          if (message.type === 'trader_activity') {
            const receivedAt = Date.now();
            const activity = {
              ...message.data,
              received_at: receivedAt,
            };
            
            // 更新最新活动
            setLastActivity(activity);
            
            // 添加到活动列表（保持最新的在前面）
            setActivities((prev) => {
              const existing = prev.find(
                (item) =>
                  (item.id && item.id === activity.id) ||
                  (item.transaction_hash && item.transaction_hash === activity.transaction_hash)
              );
              const merged = existing ? { ...activity, received_at: existing.received_at } : activity;
              const next = [
                merged,
                ...prev.filter((item) => {
                  if (merged.id && item.id === merged.id) return false;
                  if (merged.transaction_hash && item.transaction_hash === merged.transaction_hash) return false;
                  return true;
                }),
              ];
              // 限制最大数量
              return next.slice(0, MAX_ACTIVITIES);
            });
          }
        } catch (err) {
          console.error('解析 WebSocket 消息失败:', err);
        }
      };

      ws.onerror = (event) => {
        console.error('WebSocket 错误:', event);
        setError('WebSocket 连接错误');
        setIsConnected(false);
      };

      ws.onclose = () => {
        console.log('WebSocket 已断开');
        setIsConnected(false);
        wsRef.current = null;

        // 自动重连
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('尝试重新连接 WebSocket...');
          connect();
        }, RECONNECT_DELAY);
      };
    } catch (err) {
      console.error('WebSocket 连接失败:', err);
      setError('无法连接到服务器');
      setIsConnected(false);
    }
  }, []);

  useEffect(() => {
    connect();

    // 清理函数
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  return {
    activities,
    lastActivity,
    isConnected,
    error,
  };
}
